import { FC, useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import { PURCHASE_LIMIT_BEHAVIOR_TYPES } from 'src/apis/dto/DTOProduct';
import useClickProductItem from 'src/components/Shop/useClickProductItem';
import { GemProduct } from 'src/types/Payments';
import { getProductCalcGem, getProductPriceTextWithCurrency } from 'src/utils/payments';
import { hoverAndActiveCss } from 'src/utils/styled';
import { DiscountLabel, TimerLabel } from './ShopLabels';
const PriceButton = styled.div<{
  $disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 32px;
  padding: 0 12px;
  border-radius: 360px;
  background-color: ${({
  theme
}) => theme.color.surface4};
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: ${({
  theme,
  $disabled
}) => $disabled ? theme.color.content6 : theme.color.content11};
`;
const CardContainer = styled.div<{
  $disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  background-color: ${({
  theme
}) => theme.color.surface3};
  border-radius: 16px;
  overflow: hidden;
  cursor: ${({
  $disabled
}) => $disabled ? 'default' : 'pointer'};

  ${({
  theme,
  $disabled
}) => !$disabled && hoverAndActiveCss(`
      ${PriceButton} {
        background-color: ${theme.color.surface6};
      }
    `)}
`;
const ImageArea = styled.div<{
  $bgColor: string;
}>`
  width: 120px;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({
  $bgColor
}) => $bgColor};
  overflow: hidden;
  flex-shrink: 0;
`;
const ProductImage = styled.img`
  width: 140px;
  height: 73px;
  object-fit: cover;
  ${({
  theme
}) => theme.isRtl && 'transform: scaleX(-1);'}
`;
const BodyArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  padding: 12px;
  height: 100%;
  min-width: 0;
`;
const ContentsArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const LabelsRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;
const TextArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-inline-start: 2px;
`;
const GemTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content12};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const bonusGemBaseStyle = `
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const BonusGemText = styled.span`
  ${bonusGemBaseStyle}
  color: ${({
  theme
}) => theme.color.primary1};
`;
const PromoGemText = styled.span`
  ${bonusGemBaseStyle}
  color: ${({
  theme
}) => theme.color.accentF1};
`;
const DescriptionText = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  color: ${({
  theme
}) => theme.color.content8};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
interface Props {
  gemProduct: GemProduct;
  description?: string;
}
const GemProductItem: FC<Props> = ({
  gemProduct,
  description
}) => {
  const {
    t
  } = useTranslation();
  const {
    handleClickProduct
  } = useClickProductItem(gemProduct);
  const gemCount = getProductCalcGem(gemProduct);
  const priceText = getProductPriceTextWithCurrency(gemProduct);
  const [isExpired, setIsExpired] = useState(false);
  const isDisabled = isExpired || !!gemProduct.purchaseLimitExceeded && gemProduct.purchaseLimitBehavior === PURCHASE_LIMIT_BEHAVIOR_TYPES.DISABLED;
  const handleTimerOver = useCallback(() => {
    setIsExpired(true);
  }, []);
  const handleClick = () => {
    if (isDisabled) return;
    handleClickProduct();
  };
  return <CardContainer role='button' tabIndex={isDisabled ? -1 : 0} $disabled={isDisabled} onClick={handleClick} onKeyDown={e => {
    if (isDisabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClickProduct();
    }
  }} data-testid='Payments-GemProduct' data-amount={gemCount.num} data-sentry-element="CardContainer" data-sentry-component="GemProductItem" data-sentry-source-file="GemProductItem.tsx">
      <ImageArea $bgColor={gemProduct.backgroundColor} data-sentry-element="ImageArea" data-sentry-source-file="GemProductItem.tsx">
        <ProductImage src={gemProduct.iconUrl} alt='gem' data-sentry-element="ProductImage" data-sentry-source-file="GemProductItem.tsx" />
      </ImageArea>
      <BodyArea data-sentry-element="BodyArea" data-sentry-source-file="GemProductItem.tsx">
        <ContentsArea data-sentry-element="ContentsArea" data-sentry-source-file="GemProductItem.tsx">
          {(gemProduct.moduleEndDate || !!gemProduct.discountRate) && <LabelsRow>
              {gemProduct.moduleEndDate && <TimerLabel endDate={gemProduct.moduleEndDate} onTimerOver={handleTimerOver} />}
              {!!gemProduct.discountRate && <DiscountLabel percent={gemProduct.discountRate} />}
            </LabelsRow>}
          <TextArea data-sentry-element="TextArea" data-sentry-source-file="GemProductItem.tsx">
            <GemTitle data-sentry-element="GemTitle" data-sentry-source-file="GemProductItem.tsx">{`${gemCount.baseText} ${t('common__gems')}`}</GemTitle>
            {gemCount.bonus > 0 && <BonusGemText>{`+ ${gemCount.bonusText} ${t('common__gems')}`}</BonusGemText>}
            {gemCount.promo > 0 && <PromoGemText>{`+ ${gemCount.promoText} ${t('common__gems')}`}</PromoGemText>}
            {/* 
              실제로 사용되지 않으나 확장성을 위해 남겨둠
              https://hpcnt.slack.com/archives/C02SQS5AJDR/p1776758370933799?thread_ts=1776757862.716059&cid=C02SQS5AJDR
             */}
            {description && <DescriptionText>{description}</DescriptionText>}
          </TextArea>
        </ContentsArea>
        <PriceButton $disabled={isDisabled} data-sentry-element="PriceButton" data-sentry-source-file="GemProductItem.tsx">{priceText}</PriceButton>
      </BodyArea>
    </CardContainer>;
};
export default GemProductItem;